import { useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import gql from 'graphql-tag'
import Link from 'next/link'
import {
  NewsItemComment_NewsCommentEdgeFragment,
  useUpdateNewsCommentMutation,
} from 'generated/graphql'
import Button from '@app.components/Button'
import Textarea from '@app.components/inputs/Textarea'
import useExpendBtn from '@app.feature/channel/hook/useExpendBtn'
import useIsWebview from '@app.modules/hooks/useIsWebview'
import { convertStringToContent, getWebviewHref } from 'app.modules/util'
import { convertCreatedAtToString } from 'app.modules/util/time'
import Avatar from '../../../../../../app.components/Avatar/Avatar'
import NewsItemCommentForm from '../../NewsItemCommentForm/NewsItemCommentForm'
import DropdownBtn from './DropdownBtn'
import SubCommentList from './SubCommentList'
import StyledWrapper from './index.styled'

gql`
  fragment NewsItemComment_NewsCommentEdge on NewsCommentEdge {
    node {
      ...NewsComment_NewsComment
    }
    cursor
  }

  fragment NewsComment_NewsComment on NewsComment {
    id
    authorName
    content
    createdAt
    updatedAt
    deletedAt
    viewerCanReply
    viewerCanDelete
    viewerIsAuthor
    viewerHasReported
    viewerHasMuteAuthor
    author {
      ... on Channel {
        id
        url
        viewerIsOwner
        owner {
          id
          logoImage {
            id
            url
          }
        }
      }
      ... on User {
        id
        avatarUrl
      }
    }
    newsItem {
      id
      channel {
        id
      }
    }
    children {
      totalCount
    }
  }
`

type TProps = {
  type?: 'card' | 'modal'
  newsItemId: string
  viewerIsNewsAuthor: boolean
  data: NewsItemComment_NewsCommentEdgeFragment
  isRootComment?: boolean
}

const NewsItemComment: React.FC<TProps> = ({
  type = 'card',
  newsItemId,
  viewerIsNewsAuthor,
  data,
  isRootComment = true,
}) => {
  const isWebview = useIsWebview()
  const [replyOpen, setReplyOpen] = useState(false)

  const getCommentType = () => {
    if (data.node.deletedAt) return 'DELETE'
    if (data.node.viewerHasMuteAuthor) return 'MUTE'
    return 'VIEW'
  }

  const [commentType, setCommentType] = useState<
    'VIEW' | 'DELETE' | 'EDIT' | 'MUTE'
  >(getCommentType())

  const [input, setInput] = useState('')
  const [isExpended, descriptionRef, handleExpendBtnClick] = useExpendBtn()

  const updateNewsCommentMutation = useUpdateNewsCommentMutation()

  useEffect(() => {
    setCommentType(getCommentType())
  }, [data])

  const handleCancleUpdateBtn = () => {
    setCommentType('VIEW')
    setInput('')
  }

  const handleUpdateCommentBtn = () => {
    if (input === data.node.content) return handleCancleUpdateBtn()
    const [mutate] = updateNewsCommentMutation
    mutate({
      variables: {
        input: {
          id: data.node.id,
          content: input,
        },
      },
    })
    handleCancleUpdateBtn()
  }

  const handleEditMenuClick = () => {
    setCommentType('EDIT')
    setInput(data.node.content)
  }

  const handleDeleteMenuClick = () => {
    setReplyOpen(false)
  }

  const imageUrl = useMemo(() => {
    let result = ''
    if (data.node.author?.__typename === 'Channel') {
      result = data.node.author.owner?.logoImage?.url || ''
    }
    if (data.node.author?.__typename === 'User') {
      result = data.node.author.avatarUrl || ''
    }

    return result
  }, [data])

  const channelHref =
    data.node.author?.__typename === 'Channel'
      ? getWebviewHref(`/channel/${data.node.author.url}`, isWebview)
      : undefined

  const isNewsAuthor =
    data?.node?.newsItem?.channel?.id === data?.node?.author?.id
  return (
    <StyledWrapper type={type}>
      {commentType === 'VIEW' && (
        <div className="comment-view">
          <div className="user-profile">
            <div className="lt">
              <Avatar
                type={
                  data.node.author?.__typename === 'Channel'
                    ? 'channel'
                    : 'user'
                }
                size={type === 'card' ? 40 : 36}
                href={channelHref}
                src={imageUrl}
              />
              <div className="content">
                {channelHref && (
                  <Link href={channelHref}>
                    <a>
                      <div className="top">
                        <div className="user-name">{data.node.authorName}</div>
                        {isNewsAuthor && (
                          <div className="news-author">담당자</div>
                        )}
                      </div>
                    </a>
                  </Link>
                )}
                {!channelHref && (
                  <div className="top">
                    <div className="user-name">{data.node.authorName}</div>
                    {isNewsAuthor && <div className="news-author">담당자</div>}
                  </div>
                )}
                <div className="user-info">
                  <div className="date">
                    {convertCreatedAtToString(data.node.createdAt)}
                  </div>
                  {data.node.updatedAt && <div>{`(수정됨)`}</div>}
                </div>
              </div>
            </div>
            <DropdownBtn
              data={data}
              onEditMenuClick={handleEditMenuClick}
              onDeleteMenuClick={handleDeleteMenuClick}
            />
          </div>
          <div className="description">
            <div
              className={`desc-content`}
              data-expend={isExpended}
              ref={descriptionRef}
            >
              {convertStringToContent(data.node.content)}
            </div>
            {isExpended === false && (
              <a className="desc-more-btn" onClick={handleExpendBtnClick}>
                ...더보기
              </a>
            )}
          </div>
        </div>
      )}
      {commentType === 'DELETE' && (
        <div className="comment-delete">
          {convertStringToContent(data.node.content)}
        </div>
      )}
      {commentType === 'MUTE' && (
        <div className="comment-view">
          <div className="user-profile">
            <div className="comment-mute">
              {convertStringToContent(data.node.content)}
            </div>
            <DropdownBtn
              data={data}
              onEditMenuClick={handleEditMenuClick}
              onDeleteMenuClick={handleDeleteMenuClick}
            />
          </div>
        </div>
      )}
      {commentType === 'EDIT' && (
        <div className="comment-edit">
          <Avatar
            type={
              data.node.author?.__typename === 'Channel' ? 'channel' : 'user'
            }
            size={type === 'card' ? 40 : 36}
            href={channelHref}
            src={imageUrl}
          />
          <div className="rt">
            <div className="input-wrapper">
              {convertStringToContent(input)}
              <Textarea
                placeholder="댓글을 입력하세요."
                autoComplete="off"
                spellCheck={false}
                className="comment-input"
                name={'input'}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                maxLength={1000}
              />
            </div>
            <div className="btn-box">
              <Button
                color="grey"
                variant="outlined"
                onClick={handleCancleUpdateBtn}
              >
                취소
              </Button>
              <Button
                onClick={handleUpdateCommentBtn}
                disabled={input.trim() === ''}
              >
                적용
              </Button>
            </div>
          </div>
        </div>
      )}
      {isRootComment && (
        <>
          {commentType === 'VIEW' && (
            <div className="tool-box">
              {data.node.viewerCanReply === true && (
                <button
                  className="reply-btn"
                  onClick={() => setReplyOpen(!replyOpen)}
                >
                  {replyOpen ? '취소' : '답글쓰기'}
                </button>
              )}
              {data.node.children.totalCount > 0 && (
                <div className="reply-count">
                  답글 {data.node.children.totalCount}
                </div>
              )}
            </div>
          )}
          <SubCommentList
            type={type}
            newsItemId={newsItemId}
            viewerIsNewsAuthor={viewerIsNewsAuthor}
            parentId={data.node.id}
          />
          {replyOpen && (
            <NewsItemCommentForm
              className="comment-form"
              newsItemId={newsItemId}
              parentId={data.node.id}
              onSuccess={() => setReplyOpen(false)}
              viewerCanReply={data.node.viewerCanReply}
            />
          )}
        </>
      )}
    </StyledWrapper>
  )
}

export default NewsItemComment
